/* Copyright (c) 2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Link, useParams } from "react-router-dom";
import {
  Text,
  Button,
  ModalConfirm,
  Table,
  Loader,
  theme,
} from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { showSuccess } from "~/components/Toast";
import { EnvironmentRouteParams } from "~/models";
import { ServiceGroup } from "~/models/serviceGroup";
import { ROUTES } from "~/routing";
import { useAxios, generateActionSuccessText } from "~/tools";
import { ServiceShortInitialState } from "./initialState";
import { ServiceItem } from "./types";

type UnassignServiceProps = {
  refetch: () => void;
  envName: string;
  service: ServiceItem;
  setIsPopover: React.Dispatch<React.SetStateAction<boolean>>;
};

type ServiceGroupItem = {
  id: string;
  name: string;
  pricingType: string;
};

function UnassignService({
  refetch,
  envName,
  service,
  setIsPopover,
}: UnassignServiceProps) {
  const state = useParams<EnvironmentRouteParams>();
  const { environmentId } = state;
  const [isOpen, setIsOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [serviceGroups, setServiceGroups] = React.useState<ServiceGroup[]>([]);
  const [page, setPage] = React.useState(0);

  const { showError } = useShowError();
  const { Axios } = useAxios();

  const fetchServiceGroup = React.useCallback(async () => {
    try {
      const { data } = await Axios().get(
        `/catalog/serviceGroups?limit=${50}&page=${page}`
      );
      data.totalPages > 1 && data.totalPages > page + 1 && setPage(page + 1);
      data.serviceGroups &&
        setServiceGroups((prevState: ServiceGroup[]) => [
          ...prevState,
          ...data.serviceGroups,
        ]);
    } catch (e) {
      showError(e.response);
    } finally {
      setIsLoading(false);
    }
  }, [Axios, page, showError]);

  React.useEffect(() => {
    isOpen && fetchServiceGroup();
  }, [fetchServiceGroup, isOpen]);
  const isServiceGroup = !!service.serviceGroup;

  const findServiceGroup =
    serviceGroups?.find(
      (s: ServiceGroup) => s.id === service?.serviceGroup?.id
    ) || ServiceShortInitialState;

  const nameForToast = isServiceGroup
    ? service?.serviceGroup?.name
    : service?.name;

  return (
    <>
      <Button
        onClick={() => setIsOpen(true)}
        css={{ backgroundColor: theme.color.background.ui01, width: "100%" }}
        size="md"
        icon="brokenLink"
        severity="low"
      >
        Unassign
      </Button>
      <ModalConfirm
        onClose={() => setIsOpen(false)}
        isOpen={isOpen}
        confirm={async () => {
          try {
            isServiceGroup
              ? await Axios().delete(
                  `/environments/${environmentId}/serviceGroups/${service?.serviceGroup?.id}`
                )
              : await Axios().delete(
                  `/environments/${environmentId}/services/${service.id}`
                );
            setIsOpen(true);
            refetch();
            showSuccess(
              generateActionSuccessText(`The ${nameForToast}`)()("detached")()
            );
          } catch (e) {
            showError(e.response);
          } finally {
            setIsOpen(false);
            setIsPopover(false);
          }
        }}
        actionLabel="Unassign"
        contentLabel={`Unassign ${
          isServiceGroup ? "Service Group" : "Service"
        }`}
      >
        <Text color="danger">
          {`Are you sure you want to unassign ${nameForToast} from the environment
          ${envName}? Any information about modified parameters will be lost.`}
        </Text>

        {isServiceGroup && (
          <>
            <Text tag="div">List of services that will be unassigned:</Text>
            <div css={{ height: "10rem" }}>
              <Table tiny>
                <Table.thead>
                  <Table.tr>
                    <Table.th css={{ width: "100%" }}>Service Name</Table.th>
                  </Table.tr>
                </Table.thead>
                <Table.tbody>
                  {isLoading ? (
                    <Table.tr>
                      <Table.td css={{ width: "100%" }}>
                        <Loader inContent />
                      </Table.td>
                    </Table.tr>
                  ) : (
                    findServiceGroup?.services?.map(
                      (serviceGroupItem: ServiceGroupItem) => (
                        <Table.tr key={serviceGroupItem.id}>
                          <Table.td css={{ width: "100%" }}>
                            <Link
                              to={`${ROUTES.serviceCommitments.index}/${serviceGroupItem.id}`}
                            >
                              {serviceGroupItem.name}
                            </Link>
                          </Table.td>
                        </Table.tr>
                      )
                    )
                  )}
                </Table.tbody>
              </Table>
            </div>
          </>
        )}
      </ModalConfirm>
    </>
  );
}

export { UnassignService };
